/**
 * The supplier page — `/suppliers/:supplierNo/:tab`.
 *
 * One supplier concept (sup_supplier, suppliers_module). CIS owns the record and its number
 * (SUP-H40217); the Tharstern code is a cross-reference, absent for a
 * supplier made in CIS. It follows the object-page pattern approved for
 * orders and purchase orders: header (name, number, status and health pills,
 * key facts), then tabs, each its own address. A supplier has no lifecycle,
 * so there is no rail.
 *
 * Tabs: Now · Purchase orders · Purchase invoices · Coding · Integration. The account
 * (position, health, hold, Xero contact, purchase invoices, open orders, coding rules) is
 * one composed read (`/views/supplier/{no}`); the hold, release, rename and
 * coding-rule writes go to the purchase ledger's routes at the URLs it
 * carries. No Pricing tab: supplier pricing belongs to the pricing module
 * (Ben, 4 Oct). No Month end tab either (Ben, 31 Oct): a supplier's prepayments and recurring accruals
 * are See also on its One record, and the spread-over rule is on Ledger rules › Spread over.
 */

import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Link, useParams } from "react-router";
import { toast } from "sonner";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  OpenPurchaseOrder,
  PurchaseInvoiceQueuePurchaseInvoice,
  SupplierRecordView,
  WriteCodingRuleInput,
} from "@shared-core/data/purchase-ledger-types";
import {
  OPEN_ORDER_BUCKETS,
  formatDate,
  formatMoney,
} from "@shared-core/domain/purchase-ledger";
import { isFeatureEnabled } from "@shared-core/config/features";
import {
  SUPPLIER_TABS,
  paths,
  type SupplierTab,
} from "@shared-core/routing/routes";
import {
  NowPanel,
  type NowPanelItem,
} from "../app/components/patterns/NowPanel";
import { ObjectHeader } from "../app/components/patterns/ObjectHeader";
import {
  ObjectTabs,
  type ObjectTab,
} from "../app/components/patterns/ObjectTabs";
import {
  CodingRulesTable,
  NewCodingRuleDialog,
} from "../app/components/domain/purchasing/CodingRulesTab";
import { RenameSupplierButton } from "../app/components/domain/purchasing/RenameSupplierButton";
import { SupplierHoldButton } from "../app/components/domain/purchasing/SupplierHoldButton";
import { SupplierPortalAccessCard } from "../app/components/domain/purchasing/SupplierPortalAccessCard";
import {
  SupplierHealthPill,
  SupplierStatusPill,
} from "../app/components/domain/purchasing/SupplierPills";
import { Alert, AlertDescription } from "../app/components/primitives/alert";
import { Button } from "../app/components/primitives/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../app/components/primitives/card";
import { Skeleton } from "../app/components/primitives/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../app/components/primitives/table";

const TAB_LABELS: Record<SupplierTab, string> = {
  now: "Now",
  "purchase-orders": "Purchase orders",
  "purchase-invoices": "Purchase invoices",
  coding: "Coding",
  integration: "Integration",
};

type LoadState =
  | { kind: "loading" }
  | { kind: "failed"; message: string }
  | { kind: "not_found" }
  | { kind: "loaded"; account: SupplierRecordView };

export function SupplierPage() {
  const adapter = useDataAdapter();
  const { supplierNo = "", tab: rawTab } = useParams<{
    supplierNo: string;
    tab?: string;
  }>();
  const tab: SupplierTab | null = !rawTab
    ? "now"
    : (visibleTabs() as readonly string[]).includes(rawTab)
      ? (rawTab as SupplierTab)
      : null;
  const [state, setState] = useState<LoadState>({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;
    setState({ kind: "loading" });
    adapter
      .getSupplierRecord(supplierNo)
      .then((account) => {
        if (cancelled) return;
        setState(account ? { kind: "loaded", account } : { kind: "not_found" });
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setState({
            kind: "failed",
            message:
              err instanceof Error ? err.message : "The supplier did not load.",
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [adapter, supplierNo]);

  const setAccount = useCallback(
    (account: SupplierRecordView) => setState({ kind: "loaded", account }),
    [],
  );

  if (state.kind === "loading") {
    return (
      <div
        className="mx-auto max-w-7xl space-y-4"
        aria-label="Loading supplier"
      >
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }
  if (state.kind === "failed") {
    return (
      <Alert variant="destructive" className="mx-auto max-w-3xl">
        <AlertDescription>
          Could not load the supplier: {state.message}
        </AlertDescription>
      </Alert>
    );
  }
  if (state.kind === "not_found" || tab === null) {
    const missing = state.kind === "not_found";
    return (
      <div className="mx-auto max-w-3xl space-y-2 py-12 text-center">
        <h1 className="text-xl font-semibold">
          {missing ? "Supplier not found" : "Page not found"}
        </h1>
        <p className="text-muted-foreground text-sm">
          {missing
            ? `No supplier has the number “${supplierNo}”.`
            : `The supplier page has no “${rawTab}” tab.`}
        </p>
        <Link
          to={paths.suppliers()}
          className="text-primary text-sm hover:underline"
        >
          All suppliers
        </Link>
      </div>
    );
  }

  const { account } = state;
  const { position } = account;
  const missingOrders = account.openOrders.filter((po) => po.looksMissing);
  const tabs: ObjectTab[] = visibleTabs().map((id) => ({
    id,
    label: TAB_LABELS[id],
    to: paths.supplier({ supplierNo, tab: id }),
    badge:
      id === "purchase-invoices" && position.toConfirmCount > 0
        ? {
            count: position.toConfirmCount,
            tone: position.overdueCount > 0 ? "red" : "amber",
          }
        : id === "purchase-orders" && position.missingCount > 0
          ? { count: position.missingCount, tone: "amber" }
          : null,
  }));

  async function writeCodingRule(input: WriteCodingRuleInput) {
    await adapter.writeCodingRule(account.links.codingRules, input);
    const updated = await adapter.getSupplierRecord(account.supplierNo);
    toast.success("Coding rule saved");
    if (updated) setAccount(updated);
  }

  return (
    // Room at the foot so the floating Feedback button never covers a row.
    <div className="mx-auto max-w-7xl space-y-4 pb-24">
      <ObjectHeader
        crumbs={[
          { label: "Suppliers", to: paths.suppliers() },
          { label: account.name },
        ]}
        title={account.name}
        pills={
          <>
            <SupplierStatusPill status={account.status} />
            <SupplierHealthPill health={account.health} />
          </>
        }
        facts={[
          { label: "Supplier no.", value: account.supplierNo },
          {
            label: "Tharstern",
            value: account.supplierCode ?? "Not in Tharstern",
          },
          {
            label: "Terms",
            value:
              account.paymentTermsDays === null
                ? "Not recorded"
                : `${account.paymentTermsDays} days`,
          },
          {
            label: "Open POs",
            value: `${formatMoney(position.openOrdersNet)} (${position.openOrderCount})`,
          },
          {
            label: "To confirm",
            value: `${formatMoney(position.toConfirmNet)} (${position.toConfirmCount})`,
          },
          { label: "Xero contact", value: account.xeroContactLabel },
        ]}
      />
      <div className="flex justify-end gap-2">
        <RenameSupplierButton account={account} onChanged={setAccount} />
        {account.status === "active" && (
          <SupplierHoldButton account={account} onChanged={setAccount} />
        )}
      </div>
      <ObjectTabs
        tabs={tabs}
        current={tab}
        ariaLabel={`${account.name} sections`}
      />

      {tab === "now" && (
        <NowPanel
          items={nowItems(account, missingOrders, (a) => setAccount(a))}
        />
      )}
      {tab === "purchase-orders" && (
        <PurchaseOrdersTab orders={account.openOrders} />
      )}
      {tab === "purchase-invoices" && (
        <PurchaseInvoicesTab purchaseInvoices={account.purchaseInvoices} />
      )}
      {tab === "coding" && (
        <TabCard
          title="Coding"
          note={`Which Xero account ${account.name}'s purchase invoice lines code to: its own rule and the rules of the routes it is bought through. A line takes the first rule that applies: dictionary item, then route, then supplier.`}
        >
          <div className="space-y-3 px-6">
            {account.canManageRules && (
              <div className="flex justify-end">
                <NewCodingRuleDialog
                  startOn={{
                    key: account.newCodingRuleSubjectKey,
                    label: `${account.name} · ${account.supplierNo}`,
                  }}
                  onCreate={writeCodingRule}
                />
              </div>
            )}
            <CodingRulesTable
              rules={account.codingRules}
              empty={`No coding rule reaches ${account.name}'s purchase invoices yet. Its lines are coded from their dictionary item, or chosen by the reviewer.`}
            />
          </div>
        </TabCard>
      )}
      {tab === "integration" && (
        <div className="max-w-md">
          <SupplierPortalAccessCard supplierNo={account.supplierNo} />
        </div>
      )}
    </div>
  );
}

/** Integration is the supplier interface's tab: shown when its feature is on. */
function visibleTabs(): SupplierTab[] {
  return SUPPLIER_TABS.filter(
    (id) => id !== "integration" || isFeatureEnabled("supplier_portal"),
  );
}

/** What needs doing on this account, most urgent first; NowPanel orders it. */
const viewPurchaseOrder = (po: OpenPurchaseOrder) => (
  <Button asChild size="sm" variant="outline">
    <Link
      to={paths.purchaseOrderDetail({ purchaseOrderNo: po.purchaseOrderNo })}
    >
      View PO
    </Link>
  </Button>
);

function nowItems(
  account: SupplierRecordView,
  missing: OpenPurchaseOrder[],
  onChanged: (account: SupplierRecordView) => void,
): NowPanelItem[] {
  const items: NowPanelItem[] = [];
  const open = (purchaseInvoice: PurchaseInvoiceQueuePurchaseInvoice) => (
    <Button asChild size="sm" variant="outline">
      <Link
        to={paths.objectRecord({
          objectId: "finance_purchase_ledger.purchase_invoice",
          recordKey: purchaseInvoice.id,
        })}
      >
        Open
      </Link>
    </Button>
  );

  if (account.status === "on_hold") {
    const hold = account.hold;
    items.push({
      key: "on-hold",
      tone: "attention",
      lead: hold ? `On hold: ${hold.reason}` : "On hold",
      consequence:
        "purchase invoices can be matched, but not released for payment",
      when: hold ? `since ${formatDate(hold.at)}` : null,
      info: hold ? `Put on hold by ${hold.by}.` : null,
      action: <SupplierHoldButton account={account} onChanged={onChanged} />,
    });
  }
  if (account.xeroContact === "name_clash") {
    items.push({
      key: "xero-name-clash",
      tone: "attention",
      lead: `Xero contact clash: ${account.name}`,
      consequence: "nothing will post to Xero until the clash is resolved",
      info: "Xero already has a contact with this name, so One couldn't create the supplier's contact. One never links to an existing contact. Rename the supplier in One (Rename… above) or archive the Xero contact.",
      action: account.xeroClashTicketUrl ? (
        <Button asChild size="sm" variant="outline">
          <Link to={account.xeroClashTicketUrl}>Open ticket</Link>
        </Button>
      ) : undefined,
    });
  }
  const overdue = account.purchaseInvoices.filter(
    (purchaseInvoice) => purchaseInvoice.isOverdue,
  );
  for (const purchaseInvoice of overdue) {
    items.push({
      key: `overdue-${purchaseInvoice.id}`,
      tone: "attention",
      isLate: true,
      lead: `Not confirmed: ${purchaseInvoice.supplierDocumentNo}`,
      consequence: formatMoney(purchaseInvoice.netAmount),
      when: `due ${formatDate(purchaseInvoice.dueDate)}`,
      action: open(purchaseInvoice),
    });
  }
  for (const po of missing) {
    items.push({
      key: `missing-${po.purchaseOrderNo}`,
      tone: "attention",
      lead: `No invoice: ${po.purchaseOrderNo}`,
      consequence: `chase ${account.name}`,
      when: po.lastReceivedOn
        ? `received ${formatDate(po.lastReceivedOn)}`
        : null,
      action: viewPurchaseOrder(po),
    });
  }
  for (const purchaseInvoice of account.purchaseInvoices.filter(
    (b) => !b.isOverdue,
  )) {
    items.push({
      key: `confirm-${purchaseInvoice.id}`,
      tone: "do",
      lead: `${nextStepVerb(purchaseInvoice)} ${purchaseInvoice.supplierDocumentNo}`,
      when: formatDate(purchaseInvoice.receivedAt),
      info: `${formatMoney(purchaseInvoice.netAmount)} · ${purchaseInvoice.statusLabel}`,
      action: open(purchaseInvoice),
    });
  }
  for (const po of account.openOrders.filter(
    (p) => p.receipt !== "not_received" && !p.looksMissing,
  )) {
    items.push({
      key: `waiting-${po.purchaseOrderNo}`,
      tone: "waiting",
      lead: `${account.name}: invoice for ${po.purchaseOrderNo}`,
      when: po.lastReceivedOn
        ? `goods in ${formatDate(po.lastReceivedOn)}`
        : null,
    });
  }
  if (items.length === 0) {
    items.push({
      key: "done",
      tone: "done",
      lead: `Nothing outstanding with ${account.name}`,
    });
  }
  return items;
}

/**
 * The verb for a purchase invoice's next step, so the Now list never invites confirming
 * something that cannot yet be confirmed.
 */
function nextStepVerb(
  purchaseInvoice: PurchaseInvoiceQueuePurchaseInvoice,
): string {
  if (
    purchaseInvoice.documentType === "credit_note" ||
    purchaseInvoice.state === "in_query"
  ) {
    return "Review";
  }
  if (purchaseInvoice.status === "ready") return "Confirm";
  if (purchaseInvoice.status === "no_match") return "Match";
  if (purchaseInvoice.status === "non_po") return "Approve";
  return "Review";
}

function PurchaseOrdersTab({ orders }: { orders: OpenPurchaseOrder[] }) {
  const bucketLabel = Object.fromEntries(
    OPEN_ORDER_BUCKETS.map(({ bucket, label }) => [bucket, label]),
  );
  return (
    <TabCard
      title="Open purchase orders"
      note="Orders not yet invoiced in full. Settled orders drop out."
      empty={orders.length === 0 ? "No open purchase orders." : null}
    >
      <Table className="[&_td:first-child]:pl-6 [&_th:first-child]:pl-6 [&_td:last-child]:pr-6 [&_th:last-child]:pr-6">
        <TableHeader>
          <TableRow>
            <TableHead>PO</TableHead>
            <TableHead>Order</TableHead>
            <TableHead>Receipt</TableHead>
            <TableHead className="text-right">Ordered</TableHead>
            <TableHead className="w-[6rem]" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {orders.map((po) => (
            <TableRow key={po.purchaseOrderNo}>
              <TableCell className="font-medium tabular-nums">
                {po.purchaseOrderNo}
                <div className="text-muted-foreground text-xs">
                  raised {formatDate(po.raisedOn)}
                  {po.buyerName && ` by ${po.buyerName}`}
                </div>
              </TableCell>
              <TableCell>
                {po.orderNo ?? "—"}
                {po.customerName && (
                  <div className="text-muted-foreground text-xs">
                    {po.customerName}
                  </div>
                )}
              </TableCell>
              <TableCell>
                {bucketLabel[po.receipt]}
                {po.lastReceivedOn && (
                  <div className="text-muted-foreground text-xs">
                    last in {formatDate(po.lastReceivedOn)}
                  </div>
                )}
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {formatMoney(po.netAmount)}
              </TableCell>
              <TableCell className="text-right">
                {viewPurchaseOrder(po)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TabCard>
  );
}

function PurchaseInvoicesTab({
  purchaseInvoices,
}: {
  purchaseInvoices: PurchaseInvoiceQueuePurchaseInvoice[];
}) {
  return (
    <TabCard
      title="Waiting to confirm"
      note="Purchase invoices and credit notes in the confirmation queue. Once confirmed they post to Xero and leave this list."
      empty={
        purchaseInvoices.length === 0
          ? "Nothing from this supplier is waiting to confirm."
          : null
      }
    >
      <Table className="[&_td:first-child]:pl-6 [&_th:first-child]:pl-6 [&_td:last-child]:pr-6 [&_th:last-child]:pr-6">
        <TableHeader>
          <TableRow>
            <TableHead>Document</TableHead>
            <TableHead>Where it stands</TableHead>
            <TableHead>Due</TableHead>
            <TableHead className="text-right">Net</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {purchaseInvoices.map((purchaseInvoice) => (
            <TableRow key={purchaseInvoice.id}>
              <TableCell>
                <Link
                  to={paths.objectRecord({
                    objectId: "finance_purchase_ledger.purchase_invoice",
                    recordKey: purchaseInvoice.id,
                  })}
                  className="font-medium hover:underline"
                >
                  {purchaseInvoice.supplierDocumentNo}
                </Link>
                <div className="text-muted-foreground text-xs">
                  {purchaseInvoice.documentType === "credit_note"
                    ? "Credit note"
                    : "Invoice"}{" "}
                  · {formatDate(purchaseInvoice.invoiceDate)}
                </div>
              </TableCell>
              <TableCell>{purchaseInvoice.statusLabel}</TableCell>
              <TableCell
                className={
                  purchaseInvoice.isOverdue
                    ? "text-red-600 dark:text-red-400"
                    : undefined
                }
              >
                {formatDate(purchaseInvoice.dueDate)}
                {purchaseInvoice.isOverdue && (
                  <div className="text-xs">Overdue</div>
                )}
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {purchaseInvoice.documentType === "credit_note" ? "−" : ""}
                {formatMoney(purchaseInvoice.netAmount)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TabCard>
  );
}

function TabCard({
  title,
  note,
  empty,
  children,
}: {
  title: string;
  note: string;
  empty?: string | null;
  children: ReactNode;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
        <p className="text-muted-foreground text-sm">{note}</p>
      </CardHeader>
      <CardContent className="px-0">
        {empty ? (
          <p className="text-muted-foreground px-6 text-sm">{empty}</p>
        ) : (
          children
        )}
      </CardContent>
    </Card>
  );
}
